import * as React from 'react';

import axios from 'axios';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import { Button } from 'stile-shared/src/components/2020/common/Button';
import {
  Form,
  FormField,
  FormFieldError,
  Input,
} from 'stile-shared/src/components/2020/common/Form';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { Text } from 'stile-shared/src/components/2020/common/Primitives';
import { Select } from 'stile-shared/src/components/2020/common/Select';
import type { StileApiInboundLeads, StileInboundLeadAu } from 'stile-shared/src/types/services';
import { emailValidator } from 'stile-shared/src/utils/validators';

import { stateSelectOptions } from 'utils/form';

type FormData = Omit<StileInboundLeadAu['auContact'], 'referrer' | 'leadType'>;

export function EscapeRoomForm() {
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<FormData>();

  const {
    mutate,
    isLoading,
    reset: resetMutation,
    isSuccess,
    isError,
  } = useMutation(createEnquiry, {
    onSuccess: () => {
      resetForm();
    },
    retry: 5,
  });

  const onSubmit = (formData: FormData) => {
    const submissionData: StileInboundLeadAu = {
      auContact: {
        ...formData,
        referrer: window.location.href,
        leadType: 'ESCAPE_ROOM_ENQUIRY',
      },
    };
    mutate(submissionData);
  };

  // If it's before August, we want to show the current year, otherwise we want to show the next year
  const yearForAugust =
    new Date().getMonth() < 7 ? new Date().getFullYear() : new Date().getFullYear() + 1;
  // If it's August, show the different message
  const text =
    new Date().getMonth() === 7
      ? "You're in! Someone from the Stile team will be in touch shortly to set up your access to the Escape Room."
      : `Stay tuned for our August ${yearForAugust} Escape Room. In the mean time, someone from the Stile team will reach out in regards to a meeting or setting you up with your own tools to run an Escape Room.`;

  return isSuccess ? (
    <Text>{text}</Text>
  ) : (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormField>
        <Input
          {...register('firstName', {
            maxLength: { value: 100, message: '100 character limit' },
            required: 'This field is required',
          })}
          label="First name*"
          placeholder="Fay"
          aria-invalid={errors.firstName ? 'true' : 'false'}
        />
        <FormFieldError name="firstName" errors={errors} />
      </FormField>

      <FormField>
        <Input
          label="Last name*"
          placeholder="James"
          aria-invalid={errors.lastName ? 'true' : 'false'}
          {...register('lastName', {
            maxLength: { value: 100, message: '100 character limit' },
            required: 'This field is required',
          })}
        />
        <FormFieldError name="lastName" errors={errors} />
      </FormField>

      <FormField>
        <Input
          label="School name*"
          placeholder="Port Niranda High School"
          aria-invalid={errors.school ? 'true' : 'false'}
          {...register('school', {
            maxLength: { value: 200, message: '200 character limit' },
            required: 'This field is required',
          })}
        />
      </FormField>

      <FormField>
        <label htmlFor="state-select">State*</label>
        <Select
          id="state-select"
          options={stateSelectOptions}
          aria-invalid={errors.state ? 'true' : 'false'}
          {...register('state', { required: 'This field is required' })}
        />
      </FormField>

      <FormField>
        <Input
          type="email"
          label="Email*"
          aria-invalid={errors.email ? 'true' : 'false'}
          placeholder="fay.james@yourschool.vic.edu.au"
          {...register('email', {
            maxLength: { value: 200, message: '200 character limit' },
            required: 'This field is required',
            pattern: {
              value: emailValidator,
              message: 'Email address invalid',
            },
          })}
        />
        <FormFieldError name="email" errors={errors} />
      </FormField>

      {isError && (
        <Text color="error">
          Something went wrong submitting your details, please try again and if the issue persists{' '}
          <Link variant="inherit" url="/who-we-are/contact/">
            get in touch with us
          </Link>
          .
        </Text>
      )}

      <Button type="submit" onClick={resetMutation} disabled={isLoading}>
        {isLoading ? 'Submitting...' : 'Submit'}
      </Button>
    </Form>
  );
}

const createEnquiry = async (data: StileApiInboundLeads) => {
  const config = {
    headers: { 'Content-Type': 'application/json' },
  };

  const { data: response } = await axios.post(
    'https://stileapp.com/api/au/v3/inboundLeads',
    data,
    config
  );

  return response.data;
};
